// src/App.jsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  FormControlLabel,
  FormControl,
  Checkbox,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import styled from '@emotion/styled';
// import DownloadIcon from '@mui/icons-material/Download';
import {
  uploadMultipleDocuments,
  deleteDocument,
  saveSummaryDocument,
  downloadSummaryFile,
  resetAll
} from './api';
import { saveAs } from 'file-saver';

// Import Font Awesome libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash } from '@fortawesome/free-solid-svg-icons';

const Container = styled(Box)`
  background-color: #e6f3ff;
  padding: 2rem;
  border-radius: 15px;
  width: 80%;
  margin: 2rem auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const UploadArea = styled(Box)`
  background-color: #ffc8a2;
  padding: 0.8rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ffb88c;
  }
`;

const ActionButton = styled(Button)`
  background-color: #4a90e2;
  color: white;
  padding: 0.5rem 2rem;
  &:hover { background-color: #357abd; }
  &.Mui-disabled { background-color: #cccccc; }
`;

// const SummarizeButton = styled(Button)`
//   background-color: #4a90e2;
//   color: white;
//   padding: 0.5rem 2rem;
//   margin: 1rem 0;

//   &:hover {
//     background-color: #357abd;
//   }

//   &.Mui-disabled {
//     background-color: #cccccc;
//   }
// `;

const FileListContainer = styled(Box)`
  margin: 1rem 0;
`;

const FileListItem = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff5ee;
  margin-top: 0.5rem;
  padding: 0.5rem;
  border-radius: 8px;
`;

const FileName = styled(Typography)`
  margin-right: auto;
  margin-left: 0.5rem;
  font-weight: 500;
`;

const ActionIcons = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const categories = [
  'LOI',
  'IME Report',
  'Medical Record and Report',
  'Occupational and Vocational Rehabilitation Report',
  'Work Capacity Certificate Victoria',
  'Work Capacity Certificate NSW',
  'Medical Imaging',
  'Hospital Record and Report',
  'Pathology Report',
  'Claim Form',
  'Medical Certificate',
  'Surgery and Procedure Request',
  'Consent Form',
  'General Correspondence and Miscellaneous Document',
  'Legal Document',
  'Form AMS',
  'Criminal, Police and Corrective',
  'CenterLink Medical Certificate',
  'Educational Record',
  'Factual Investigation',
  'IMC Referral Form',
  'Allied Health Recovery Request',
  'Surveillance Investigation',
  'Injury Management Plan'
];

const App = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isVision, setIsVision] = useState(false);
  const [summaryDocName, setSummaryDocName] = useState('');

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  /**
   * Handle multiple file selection from the input box
   */
  const handleFileSelect = async (e) => {
    e.preventDefault();
    const selectedFiles = e.target.files; // This is a FileList object

    if (!selectedFiles || selectedFiles.length === 0) return;

    // Filter out only PDF or Word files
    const validFiles = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      if (
        file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      try {
        setIsUploading(true);
        // Send valid files to our "uploadMultipleDocuments" API
        const response = await uploadMultipleDocuments(validFiles);
        // The server might return an array of uploaded file data
        setUploadedFiles((prev) => [...prev, ...response]);
      } catch (error) {
        console.error('Multiple upload failed:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  /**
   * Handle multiple file drag-and-drop
   */
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files; // This is a FileList object

    if (!droppedFiles || droppedFiles.length === 0) return;

    // Filter out only PDF or Word files
    const validFiles = [];
    for (let i = 0; i < droppedFiles.length; i++) {
      const file = droppedFiles[i];
      if (
        file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      try {
        setIsUploading(true);
        const response = await uploadMultipleDocuments(validFiles);
        setUploadedFiles((prev) => [...prev, ...response]);
      } catch (error) {
        console.error('Multiple upload failed:', error);
      } finally {
        setIsUploading(false);
      }
    }
  }, []);

  const handleSummarize = async () => {
    if (!uploadedFiles.length) return;
    setIsProcessing(true);
    try {
      const fileNames = uploadedFiles.map(f => f.fileName);
      const docFileNameObj = await saveSummaryDocument(
        fileNames, selectedCategory, isVision
      );
      console.log("Save Result: ", docFileNameObj)
      console.log("Save Result: ", docFileNameObj.docFileName)
      setSummaryDocName(docFileNameObj.docFileName);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!summaryDocName) return;
    setIsProcessing(true);
    try {
      const blob = await downloadSummaryFile(summaryDocName);
      saveAs(blob, summaryDocName);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    // hard reset on server UI
    setIsProcessing(true);
    resetAll()
      .then(() => {
        setUploadedFiles([]);
        setSummaryDocName('');
        setSelectedCategory('');
        setIsVision(false);
      })
      .catch(err => console.error('Reset failed:', err))
      .finally(() => setIsProcessing(false));
  };

  // Handle file deletion
  const handleDelete = async (fileName) => {
    try {
      await deleteDocument(fileName);
      setUploadedFiles((prev) => prev.filter((file) => file.fileName !== fileName));
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  return (
    <Container>
      {/* ============ CATEGORY SELECTION ============ */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Category
      </Typography>
      <FormControl fullWidth sx={{ bgcolor: 'white', mb: 2 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={selectedCategory}
          label="Category"
          onChange={handleCategoryChange}
        >
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <UploadArea
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        component="label"
      >
        <input
          type="file"
          multiple
          hidden
          accept=".pdf,.doc,.docx"
          onClick={(e) => (e.target.value = '')}
          onChange={handleFileSelect}
        />

        {/* Use the FontAwesomeIcon component for the upload icon */}
        <FontAwesomeIcon icon={faUpload} style={{ fontSize: '24px' }} />
      </UploadArea>

      {/* ============ UPLOADING INDICATOR OR FILE LIST ============ */}
      {isUploading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        uploadedFiles.length > 0 && (
          // <FileListContainer>
          //   {uploadedFiles.map(({ fileName, summary, error }, index) => (
          //     <React.Fragment key={fileName + index}>
          //       <FileListItem>
          //         <FileName variant="body2">{fileName}</FileName>

          //         <ActionIcons>
          //           {/* If summary exists, show "Download Summary" button */}
          //           {summary && (
          //             <Button
          //               variant="contained"
          //               size="small"
          //               startIcon={<DownloadIcon />}
          //               onClick={() => handleDownloadSummary(fileName)}
          //             >
          //               Download Summary
          //             </Button>
          //           )}

          //           {/* DELETE FILE */}
          //           <FontAwesomeIcon
          //             icon={faTrash}
          //             color="red"
          //             style={{ cursor: 'pointer' }}
          //             onClick={() => handleDelete(fileName)}
          //           />
          //         </ActionIcons>
          //       </FileListItem>
          //     </React.Fragment>
          //   ))}
          // </FileListContainer>
          <FileListContainer>
            {uploadedFiles.map(({ fileName }, idx) => (
              <FileListItem key={fileName + idx}>
                <FileName>{fileName}</FileName>
                <ActionIcons>
                  <FontAwesomeIcon
                    icon={faTrash}
                    color="red"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleDelete(fileName)}
                  />
                </ActionIcons>
              </FileListItem>
            ))}
          </FileListContainer>
        )
      )}

      {(uploadedFiles.length > 0) && (
        <Box sx={{ mt: 1, ml: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isVision}
                onChange={(e) => setIsVision(e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
              />
            }
            label={
              <Typography variant="body2">
                Summarize by Vision LLM: for Work Capacity or other forms
              </Typography>
            }
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}

      {/* ============ SINGLE TOGGLING BUTTON ============ */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <SummarizeButton
          disabled={!uploadedFiles.length || isProcessing || isUploading}
          onClick={hasSummaries ? handleCleanSummaries : handleSummarize}
        >
          {isProcessing ? (
            <CircularProgress size={24} />
          ) : hasSummaries ? (
            'Reset'
          ) : (
            'Summarize'
          )}
        </SummarizeButton>
      </Box> */}
      <Box display="flex" justifyContent="center" gap={2} mt={2}>
        {!summaryDocName ? (
          <ActionButton
            disabled={
              !uploadedFiles.length  // no files
              || !selectedCategory   // no category
              || isProcessing
            }
            onClick={handleSummarize}
          >
            {isProcessing
              ? <CircularProgress size={24} />
              : 'Summarize'
            }
          </ActionButton>
        ) : (
          <>
            <ActionButton disabled={isProcessing} onClick={handleDownload}>
              {isProcessing
                ? <CircularProgress size={24} />
                : 'Download Summary'
              }
            </ActionButton>

            <ActionButton
              disabled={isProcessing}
              onClick={handleReset}
              sx={{
                backgroundColor: '#f44336',
                '&:hover': { backgroundColor: '#d32f2f' }
              }}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'Reset'}
            </ActionButton>
          </>
        )}
      </Box>
    </Container>
  );
};

export default App;
